/**
 * Schema type field specs.
 *
 * One entry per schema.org type we support. Each field is tagged with the tier
 * Google actually assigns it, so the builder can tell a visitor the difference
 * between "this will not be eligible for a rich result" and "this is merely
 * nice to have".
 *
 * `richResult` is deliberately honest. Several generators still advertise rich
 * results that Google retired (HowTo, Sept 2023) or restricted to a handful of
 * site types (FAQ, Aug 2023). Saying so is the point: the markup is still worth
 * adding because answer engines read it, but we do not promise a star rating
 * that will never appear.
 *
 * Sources: Google Search Central structured data docs and schema.org.
 */

export type FieldKind =
  | "text"
  | "textarea"
  | "url"
  | "email"
  | "tel"
  | "date"
  | "datetime"
  | "time"
  | "number"
  | "select"
  | "duration"
  /** A single nested entity, e.g. address → PostalAddress. */
  | "group"
  /** A repeatable list of nested entities, e.g. FAQ questions. */
  | "list"
  /** A repeatable list of plain strings, e.g. sameAs. */
  | "stringList";

/**
 * required    — omit it and Google will not render the rich result at all.
 * recommended — Google names it as recommended; improves eligibility/appearance.
 * ai          — not a Google requirement, but it is what answer engines lean on
 *               to identify and cite the entity. This tier is our addition.
 */
export type FieldTier = "required" | "recommended" | "ai";

export interface FieldSpec {
  /** JSON-LD property name, emitted verbatim. */
  key: string;
  label: string;
  kind: FieldKind;
  tier: FieldTier;
  help?: string;
  placeholder?: string;
  /** select only */
  options?: Array<{ value: string; label: string }>;
  /** group and list only */
  fields?: FieldSpec[];
  /** group and list only: the @type stamped on each nested entity. */
  itemType?: string;
  /** list only: label for the add button, e.g. "Add question". */
  addLabel?: string;
  /** list only: how many blank rows to start with. */
  initialRows?: number;
}

/**
 * How Google currently treats the rich result for this type.
 *
 * active   — still rendered in search results.
 * limited  — rendered only for a restricted set of sites.
 * retired  — Google removed the rich result; the markup still carries meaning.
 * none     — never was a rich result; it exists for entity understanding.
 */
export type RichResultStatus = "active" | "limited" | "retired" | "none";

export interface SchemaTypeSpec {
  /** Stable key used in state, URLs and the picker. */
  id: string;
  /** The @type value emitted. */
  type: string;
  label: string;
  /** Picker grouping. */
  group: string;
  /** One line on what this type is for, written for a non-developer. */
  blurb: string;
  richResultStatus: RichResultStatus;
  /** Shown next to the status badge. Must stay factually accurate. */
  richResultNote: string;
  /** Types that belong in the graph alongside this one. */
  suggests?: string[];
  fields: FieldSpec[];
}

// ---------------------------------------------------------------------------
// Shared field fragments
// ---------------------------------------------------------------------------

const postalAddress: FieldSpec = {
  key: "address",
  label: "Street address",
  kind: "group",
  tier: "required",
  itemType: "PostalAddress",
  help: "Google needs a real, complete postal address to match you to a place.",
  fields: [
    {
      key: "streetAddress",
      label: "Street",
      kind: "text",
      tier: "required",
      placeholder: "124 Main Street, Suite 3",
    },
    { key: "addressLocality", label: "City", kind: "text", tier: "required", placeholder: "Austin" },
    {
      key: "addressRegion",
      label: "State or region",
      kind: "text",
      tier: "required",
      placeholder: "TX",
    },
    { key: "postalCode", label: "Postal code", kind: "text", tier: "required", placeholder: "78701" },
    {
      key: "addressCountry",
      label: "Country",
      kind: "text",
      tier: "required",
      placeholder: "US",
      help: "Two-letter country code.",
    },
  ],
};

const geoCoordinates: FieldSpec = {
  key: "geo",
  label: "Map coordinates",
  kind: "group",
  tier: "recommended",
  itemType: "GeoCoordinates",
  help: "Pins you precisely. Grab these from the URL of your Google Maps listing.",
  fields: [
    { key: "latitude", label: "Latitude", kind: "text", tier: "recommended", placeholder: "30.2672" },
    { key: "longitude", label: "Longitude", kind: "text", tier: "recommended", placeholder: "-97.7431" },
  ],
};

const openingHours: FieldSpec = {
  key: "openingHoursSpecification",
  label: "Opening hours",
  kind: "list",
  tier: "recommended",
  itemType: "OpeningHoursSpecification",
  addLabel: "Add hours",
  help: "One row per set of days that share the same hours.",
  fields: [
    {
      key: "dayOfWeek",
      label: "Days",
      kind: "stringList",
      tier: "recommended",
      help: "Monday, Tuesday, and so on.",
    },
    { key: "opens", label: "Opens", kind: "time", tier: "recommended", placeholder: "09:00" },
    { key: "closes", label: "Closes", kind: "time", tier: "recommended", placeholder: "17:00" },
  ],
};

const sameAs: FieldSpec = {
  key: "sameAs",
  label: "Profiles that prove this is you",
  kind: "stringList",
  tier: "ai",
  help: "Your LinkedIn, Facebook, X, Instagram, Crunchbase, Wikipedia or Wikidata page. This is the single strongest signal that an answer engine has matched the right entity.",
  placeholder: "https://www.linkedin.com/company/your-company",
};

const aggregateRating: FieldSpec = {
  key: "aggregateRating",
  label: "Average rating",
  kind: "group",
  tier: "recommended",
  itemType: "AggregateRating",
  help: "Only mark up ratings that are genuinely displayed on the page. Google treats invented ratings as spam.",
  fields: [
    { key: "ratingValue", label: "Rating", kind: "text", tier: "required", placeholder: "4.8" },
    { key: "reviewCount", label: "Number of reviews", kind: "number", tier: "required", placeholder: "127" },
    { key: "bestRating", label: "Best possible", kind: "number", tier: "recommended", placeholder: "5" },
    { key: "worstRating", label: "Worst possible", kind: "number", tier: "recommended", placeholder: "1" },
  ],
};

const authorField: FieldSpec = {
  key: "author",
  label: "Author",
  kind: "group",
  tier: "recommended",
  itemType: "Person",
  help: "Answer engines weigh author identity heavily when deciding whether to cite a page. A name with a link to a real bio beats a name alone.",
  fields: [
    { key: "name", label: "Author name", kind: "text", tier: "required", placeholder: "Jane Doe" },
    {
      key: "url",
      label: "Author bio page",
      kind: "url",
      tier: "ai",
      placeholder: "https://yoursite.com/about/jane-doe",
    },
  ],
};

const offersField = (tier: FieldTier = "required"): FieldSpec => ({
  key: "offers",
  label: "Price and availability",
  kind: "group",
  tier,
  itemType: "Offer",
  fields: [
    { key: "price", label: "Price", kind: "text", tier: "required", placeholder: "49.00" },
    {
      key: "priceCurrency",
      label: "Currency",
      kind: "text",
      tier: "required",
      placeholder: "USD",
      help: "Three-letter currency code.",
    },
    {
      key: "availability",
      label: "Availability",
      kind: "select",
      tier: "required",
      options: [
        { value: "https://schema.org/InStock", label: "In stock" },
        { value: "https://schema.org/OutOfStock", label: "Out of stock" },
        { value: "https://schema.org/PreOrder", label: "Pre-order" },
        { value: "https://schema.org/BackOrder", label: "Back-order" },
        { value: "https://schema.org/Discontinued", label: "Discontinued" },
      ],
    },
    { key: "url", label: "Buy or booking link", kind: "url", tier: "recommended" },
    { key: "priceValidUntil", label: "Price valid until", kind: "date", tier: "recommended" },
  ],
});

// ---------------------------------------------------------------------------
// LocalBusiness subtypes
//
// Using the most specific subtype that fits is the cheapest accuracy win in
// schema. "Dentist" tells a machine far more than "LocalBusiness".
//
// Every value below is a real schema.org class that genuinely inherits from
// LocalBusiness, verified against the published vocabulary by
// scripts/verify-schema-vocab.ts. That check exists because it is tempting to
// invent plausible-sounding types: schema.org has no Landscaper, no CleaningService,
// no Photographer and no FuneralHome, and emitting one produces markup that is
// valid JSON-LD while meaning precisely nothing. Trades with no class of their own
// are mapped to their real parent instead, which is less specific but true.
//
// Values are unique, so no two labels resolve to the same @type.
// Run the verify script after editing this list.
// ---------------------------------------------------------------------------

export const LOCAL_BUSINESS_SUBTYPES: Array<{ value: string; label: string }> = [
  // General
  { value: "LocalBusiness", label: "General local business" },
  { value: "ProfessionalService", label: "Professional service (agency, consultant, photographer)" },

  // Trades and home services
  { value: "HomeAndConstructionBusiness", label: "Home services (cleaning, landscaping, pest control)" },
  { value: "Plumber", label: "Plumber" },
  { value: "Electrician", label: "Electrician" },
  { value: "HVACBusiness", label: "HVAC" },
  { value: "RoofingContractor", label: "Roofing contractor" },
  { value: "GeneralContractor", label: "General contractor" },
  { value: "HousePainter", label: "House painter" },
  { value: "Locksmith", label: "Locksmith" },
  { value: "MovingCompany", label: "Moving company" },
  { value: "SelfStorage", label: "Self storage" },

  // Legal, finance and property
  { value: "LegalService", label: "Legal service" },
  { value: "Attorney", label: "Law firm or attorney" },
  { value: "Notary", label: "Notary" },
  { value: "AccountingService", label: "Accountant or bookkeeper" },
  { value: "FinancialService", label: "Financial service" },
  { value: "InsuranceAgency", label: "Insurance agency" },
  { value: "BankOrCreditUnion", label: "Bank or credit union" },
  { value: "RealEstateAgent", label: "Real estate agent" },
  { value: "EmploymentAgency", label: "Employment or recruitment agency" },

  // Medical
  { value: "MedicalBusiness", label: "Medical business (including veterinary)" },
  { value: "MedicalClinic", label: "Medical clinic" },
  { value: "PhysiciansOffice", label: "Doctor's office" },
  { value: "Physician", label: "Physician" },
  { value: "Dentist", label: "Dentist" },
  { value: "Optician", label: "Optician" },
  { value: "Pharmacy", label: "Pharmacy" },
  { value: "Hospital", label: "Hospital" },

  // Health and beauty
  { value: "HealthAndBeautyBusiness", label: "Health and beauty" },
  { value: "BeautySalon", label: "Beauty salon" },
  { value: "HairSalon", label: "Hair salon" },
  { value: "NailSalon", label: "Nail salon" },
  { value: "DaySpa", label: "Day spa" },
  { value: "TattooParlor", label: "Tattoo parlor" },
  { value: "HealthClub", label: "Health club" },
  { value: "ExerciseGym", label: "Gym" },
  { value: "SportsClub", label: "Sports club" },
  { value: "SportsActivityLocation", label: "Sports or activity venue" },

  // Food and drink
  { value: "FoodEstablishment", label: "Food establishment" },
  { value: "Restaurant", label: "Restaurant" },
  { value: "CafeOrCoffeeShop", label: "Cafe or coffee shop" },
  { value: "Bakery", label: "Bakery" },
  { value: "BarOrPub", label: "Bar or pub" },
  { value: "FastFoodRestaurant", label: "Fast food" },
  { value: "IceCreamShop", label: "Ice cream shop" },
  { value: "Brewery", label: "Brewery" },
  { value: "Distillery", label: "Distillery" },

  // Retail
  { value: "Store", label: "Shop or store" },
  { value: "ClothingStore", label: "Clothing store" },
  { value: "ShoeStore", label: "Shoe store" },
  { value: "JewelryStore", label: "Jewelry store" },
  { value: "GroceryStore", label: "Grocery store" },
  { value: "ConvenienceStore", label: "Convenience store" },
  { value: "LiquorStore", label: "Liquor store" },
  { value: "HardwareStore", label: "Hardware store" },
  { value: "GardenStore", label: "Garden centre" },
  { value: "FurnitureStore", label: "Furniture store" },
  { value: "HomeGoodsStore", label: "Home goods store" },
  { value: "ElectronicsStore", label: "Electronics store" },
  { value: "ComputerStore", label: "Computer store or IT repair" },
  { value: "MobilePhoneStore", label: "Mobile phone store" },
  { value: "BookStore", label: "Book store" },
  { value: "MusicStore", label: "Music store" },
  { value: "ToyStore", label: "Toy store" },
  { value: "HobbyShop", label: "Hobby shop" },
  { value: "SportingGoodsStore", label: "Sporting goods store" },
  { value: "BikeStore", label: "Bike shop" },
  { value: "PetStore", label: "Pet store" },
  { value: "Florist", label: "Florist" },
  { value: "OfficeEquipmentStore", label: "Office equipment store" },
  { value: "DepartmentStore", label: "Department store" },
  { value: "PawnShop", label: "Pawn shop" },
  { value: "WholesaleStore", label: "Wholesale store" },
  { value: "ShoppingCenter", label: "Shopping centre" },

  // Automotive
  { value: "AutomotiveBusiness", label: "Automotive business" },
  { value: "AutoRepair", label: "Auto repair" },
  { value: "AutoBodyShop", label: "Auto body shop" },
  { value: "AutoDealer", label: "Car dealership" },
  { value: "AutoRental", label: "Car rental" },
  { value: "AutoWash", label: "Car wash" },
  { value: "AutoPartsStore", label: "Auto parts store" },
  { value: "TireShop", label: "Tire shop" },
  { value: "GasStation", label: "Gas station" },
  { value: "MotorcycleDealer", label: "Motorcycle dealer" },
  { value: "MotorcycleRepair", label: "Motorcycle repair" },

  // Lodging and travel
  { value: "LodgingBusiness", label: "Lodging business" },
  { value: "Hotel", label: "Hotel" },
  { value: "Motel", label: "Motel" },
  { value: "Hostel", label: "Hostel" },
  { value: "BedAndBreakfast", label: "Bed and breakfast" },
  { value: "Resort", label: "Resort" },
  { value: "VacationRental", label: "Vacation rental" },
  { value: "Campground", label: "Campground" },
  { value: "TravelAgency", label: "Travel agency" },

  // Entertainment and leisure
  { value: "EntertainmentBusiness", label: "Entertainment venue" },
  { value: "MovieTheater", label: "Movie theater" },
  { value: "NightClub", label: "Night club" },
  { value: "ComedyClub", label: "Comedy club" },
  { value: "ArtGallery", label: "Art gallery" },
  { value: "AmusementPark", label: "Amusement park" },
  { value: "BowlingAlley", label: "Bowling alley" },
  { value: "GolfCourse", label: "Golf course" },
  { value: "Casino", label: "Casino" },

  // Other services
  { value: "ChildCare", label: "Childcare" },
  { value: "DryCleaningOrLaundry", label: "Dry cleaning or laundry" },
  { value: "AnimalShelter", label: "Animal shelter" },
  { value: "RecyclingCenter", label: "Recycling centre" },
  { value: "InternetCafe", label: "Internet cafe" },
  { value: "Library", label: "Library" },
  { value: "EmergencyService", label: "Emergency service" },
];

// ---------------------------------------------------------------------------
// The type registry
// ---------------------------------------------------------------------------

export const SCHEMA_TYPES: SchemaTypeSpec[] = [
  // -- Business -------------------------------------------------------------
  {
    id: "LocalBusiness",
    type: "LocalBusiness",
    label: "Local business",
    group: "Business",
    blurb: "A business customers visit or that serves a specific area.",
    richResultStatus: "active",
    richResultNote:
      "Can earn a place result in Google Search and Maps when the address and hours match your Business Profile.",
    suggests: ["Organization", "WebSite", "BreadcrumbList"],
    fields: [
      {
        key: "@subtype",
        label: "What kind of business",
        kind: "select",
        tier: "recommended",
        options: LOCAL_BUSINESS_SUBTYPES,
        help: "Pick the most specific match. A specific subtype tells a machine far more than the generic one. If your exact trade is not here, choose the closest parent: schema.org has no class for every trade, and a broader type that is real beats a specific one that is invented.",
      },
      { key: "name", label: "Business name", kind: "text", tier: "required", placeholder: "Northside Dental" },
      {
        key: "url",
        label: "Website",
        kind: "url",
        tier: "required",
        placeholder: "https://northsidedental.com",
      },
      postalAddress,
      { key: "telephone", label: "Phone", kind: "tel", tier: "recommended", placeholder: "+1-512-555-0142" },
      {
        key: "image",
        label: "Photo of the business",
        kind: "url",
        tier: "recommended",
        help: "A real, publicly reachable photo. Not a logo, and not a file behind a login.",
      },
      {
        key: "description",
        label: "What the business does",
        kind: "textarea",
        tier: "ai",
        help: "Two or three plain sentences. This is often the text an answer engine quotes back.",
      },
      {
        key: "priceRange",
        label: "Price range",
        kind: "text",
        tier: "recommended",
        placeholder: "$$",
      },
      openingHours,
      geoCoordinates,
      { key: "email", label: "Email", kind: "email", tier: "recommended" },
      {
        key: "areaServed",
        label: "Areas served",
        kind: "stringList",
        tier: "ai",
        help: "Cities or regions you cover. Answer engines use this for \"near me\" style questions.",
        placeholder: "Austin, TX",
      },
      aggregateRating,
      sameAs,
    ],
  },
  {
    id: "Organization",
    type: "Organization",
    label: "Organization",
    group: "Business",
    blurb: "The company behind the site. Belongs on every site, once.",
    richResultStatus: "active",
    richResultNote:
      "Feeds the knowledge panel and the logo shown beside your results. Also the anchor every other type links back to.",
    suggests: ["WebSite"],
    fields: [
      { key: "name", label: "Organization name", kind: "text", tier: "required", placeholder: "Acme Studio" },
      { key: "url", label: "Website", kind: "url", tier: "required", placeholder: "https://acmestudio.com" },
      {
        key: "logo",
        label: "Logo",
        kind: "url",
        tier: "recommended",
        help: "Publicly reachable image, at least 112x112 pixels.",
      },
      {
        key: "description",
        label: "What the organization does",
        kind: "textarea",
        tier: "ai",
      },
      { key: "telephone", label: "Phone", kind: "tel", tier: "recommended" },
      { key: "email", label: "Email", kind: "email", tier: "recommended" },
      { ...postalAddress, tier: "recommended" },
      { key: "foundingDate", label: "Founded", kind: "date", tier: "ai" },
      sameAs,
    ],
  },
  {
    id: "Service",
    type: "Service",
    label: "Service",
    group: "Business",
    blurb: "A single service you sell, with its own page.",
    richResultStatus: "none",
    richResultNote:
      "Not a Google rich result. Worth adding anyway: it is how a machine learns which services you offer and where.",
    suggests: ["Organization", "LocalBusiness", "BreadcrumbList"],
    fields: [
      {
        key: "name",
        label: "Service name",
        kind: "text",
        tier: "required",
        placeholder: "Emergency drain unblocking",
      },
      {
        key: "description",
        label: "What the service includes",
        kind: "textarea",
        tier: "recommended",
      },
      {
        key: "serviceType",
        label: "Service category",
        kind: "text",
        tier: "recommended",
        placeholder: "Plumbing",
      },
      { key: "url", label: "Service page", kind: "url", tier: "recommended" },
      {
        key: "areaServed",
        label: "Areas served",
        kind: "stringList",
        tier: "ai",
        placeholder: "Austin, TX",
      },
      { key: "image", label: "Image", kind: "url", tier: "recommended" },
      offersField("recommended"),
    ],
  },

  // -- Content --------------------------------------------------------------
  {
    id: "Article",
    type: "Article",
    label: "Article or blog post",
    group: "Content",
    blurb: "A post, guide or news piece.",
    richResultStatus: "active",
    richResultNote:
      "Feeds article treatments and Top Stories. The author and date fields are also what answer engines check before citing a page.",
    suggests: ["Organization", "WebSite", "BreadcrumbList", "FAQPage"],
    fields: [
      {
        key: "@subtype",
        label: "Kind of article",
        kind: "select",
        tier: "recommended",
        options: [
          { value: "Article", label: "Article" },
          { value: "BlogPosting", label: "Blog post" },
          { value: "NewsArticle", label: "News article" },
          { value: "TechArticle", label: "Technical article" },
          { value: "ScholarlyArticle", label: "Scholarly article" },
        ],
      },
      {
        key: "headline",
        label: "Headline",
        kind: "text",
        tier: "required",
        help: "Keep it under 110 characters. Google truncates past that.",
      },
      { key: "url", label: "Page URL", kind: "url", tier: "recommended" },
      {
        key: "image",
        label: "Featured image",
        kind: "url",
        tier: "recommended",
        help: "At least 1200 pixels wide for the best treatment.",
      },
      { key: "datePublished", label: "Published", kind: "datetime", tier: "recommended" },
      { key: "dateModified", label: "Last updated", kind: "datetime", tier: "recommended" },
      authorField,
      {
        key: "description",
        label: "Summary",
        kind: "textarea",
        tier: "ai",
        help: "A two to four sentence standalone answer. This is the passage most likely to be quoted.",
      },
      {
        key: "articleSection",
        label: "Section or category",
        kind: "text",
        tier: "ai",
      },
      {
        key: "keywords",
        label: "Topics covered",
        kind: "stringList",
        tier: "ai",
        help: "The subjects this piece genuinely covers.",
      },
    ],
  },
  {
    id: "FAQPage",
    type: "FAQPage",
    label: "FAQ",
    group: "Content",
    blurb: "A page of questions and answers.",
    richResultStatus: "limited",
    richResultNote:
      "Since August 2023 Google shows FAQ rich results only for well-known government and health sites. The markup is still worth adding: it hands answer engines clean question and answer pairs, which is exactly the shape they retrieve.",
    suggests: ["Organization", "WebSite"],
    fields: [
      {
        key: "mainEntity",
        label: "Questions",
        kind: "list",
        tier: "required",
        itemType: "Question",
        addLabel: "Add question",
        initialRows: 2,
        help: "Every question must appear in the visible page text too. Marking up answers a visitor cannot see is a violation.",
        fields: [
          {
            key: "name",
            label: "Question",
            kind: "text",
            tier: "required",
            placeholder: "How long does a filling take?",
          },
          {
            key: "acceptedAnswer.text",
            label: "Answer",
            kind: "textarea",
            tier: "required",
            placeholder: "Most fillings take 20 to 40 minutes from start to finish.",
          },
        ],
      },
    ],
  },
  {
    id: "HowTo",
    type: "HowTo",
    label: "How-to",
    group: "Content",
    blurb: "Step by step instructions.",
    richResultStatus: "retired",
    richResultNote:
      "Google retired the HowTo rich result in 2023, so this will not add anything visual to a search listing. It still describes your steps to an answer engine in a structured way.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "name", label: "Task name", kind: "text", tier: "required", placeholder: "How to reset a breaker" },
      { key: "description", label: "Overview", kind: "textarea", tier: "recommended" },
      { key: "image", label: "Image", kind: "url", tier: "recommended" },
      {
        key: "totalTime",
        label: "Total time",
        kind: "duration",
        tier: "recommended",
        placeholder: "PT15M",
        help: "ISO 8601 duration. PT15M is fifteen minutes, PT1H30M is an hour and a half.",
      },
      {
        key: "step",
        label: "Steps",
        kind: "list",
        tier: "required",
        itemType: "HowToStep",
        addLabel: "Add step",
        initialRows: 3,
        fields: [
          { key: "name", label: "Step title", kind: "text", tier: "required" },
          { key: "text", label: "What to do", kind: "textarea", tier: "required" },
          { key: "url", label: "Link to this step", kind: "url", tier: "recommended" },
          { key: "image", label: "Step image", kind: "url", tier: "recommended" },
        ],
      },
      {
        key: "supply",
        label: "Supplies needed",
        kind: "stringList",
        tier: "recommended",
      },
      { key: "tool", label: "Tools needed", kind: "stringList", tier: "recommended" },
    ],
  },
  {
    id: "Recipe",
    type: "Recipe",
    label: "Recipe",
    group: "Content",
    blurb: "A dish with ingredients and method.",
    richResultStatus: "active",
    richResultNote: "Can earn recipe rich results and the recipe carousel.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "name", label: "Recipe name", kind: "text", tier: "required" },
      { key: "image", label: "Finished dish photo", kind: "url", tier: "required" },
      {
        key: "recipeIngredient",
        label: "Ingredients",
        kind: "stringList",
        tier: "required",
        placeholder: "200g plain flour",
      },
      {
        key: "recipeInstructions",
        label: "Method",
        kind: "list",
        tier: "required",
        itemType: "HowToStep",
        addLabel: "Add step",
        initialRows: 3,
        fields: [{ key: "text", label: "Step", kind: "textarea", tier: "required" }],
      },
      { key: "description", label: "Description", kind: "textarea", tier: "recommended" },
      authorField,
      { key: "prepTime", label: "Prep time", kind: "duration", tier: "recommended", placeholder: "PT20M" },
      { key: "cookTime", label: "Cook time", kind: "duration", tier: "recommended", placeholder: "PT35M" },
      { key: "totalTime", label: "Total time", kind: "duration", tier: "recommended", placeholder: "PT55M" },
      { key: "recipeYield", label: "Servings", kind: "text", tier: "recommended", placeholder: "4 servings" },
      { key: "recipeCategory", label: "Category", kind: "text", tier: "recommended", placeholder: "Dessert" },
      { key: "recipeCuisine", label: "Cuisine", kind: "text", tier: "recommended", placeholder: "Italian" },
      { key: "datePublished", label: "Published", kind: "date", tier: "recommended" },
      aggregateRating,
    ],
  },
  {
    id: "VideoObject",
    type: "VideoObject",
    label: "Video",
    group: "Content",
    blurb: "A video hosted or embedded on the page.",
    richResultStatus: "active",
    richResultNote: "Can earn a video thumbnail in Search and inclusion in the video tab.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "name", label: "Video title", kind: "text", tier: "required" },
      { key: "description", label: "Description", kind: "textarea", tier: "required" },
      {
        key: "thumbnailUrl",
        label: "Thumbnail",
        kind: "url",
        tier: "required",
        help: "Publicly reachable image, at least 1200 pixels wide.",
      },
      {
        key: "uploadDate",
        label: "Upload date",
        kind: "datetime",
        tier: "required",
      },
      { key: "duration", label: "Duration", kind: "duration", tier: "recommended", placeholder: "PT4M32S" },
      { key: "contentUrl", label: "Direct video file", kind: "url", tier: "recommended" },
      { key: "embedUrl", label: "Embed URL", kind: "url", tier: "recommended" },
    ],
  },

  // -- Commerce -------------------------------------------------------------
  {
    id: "Product",
    type: "Product",
    label: "Product",
    group: "Commerce",
    blurb: "Something you sell, with a price.",
    richResultStatus: "active",
    richResultNote:
      "Can earn price, availability and review snippets. Needs an offer, a review or a rating to qualify.",
    suggests: ["Organization", "WebSite", "BreadcrumbList"],
    fields: [
      { key: "name", label: "Product name", kind: "text", tier: "required" },
      {
        key: "image",
        label: "Product image",
        kind: "url",
        tier: "recommended",
        help: "Publicly reachable. A staging or localhost URL will fail.",
      },
      { key: "description", label: "Description", kind: "textarea", tier: "recommended" },
      {
        key: "brand",
        label: "Brand",
        kind: "group",
        tier: "recommended",
        itemType: "Brand",
        fields: [{ key: "name", label: "Brand name", kind: "text", tier: "required" }],
      },
      { key: "sku", label: "SKU", kind: "text", tier: "recommended" },
      {
        key: "gtin",
        label: "GTIN, UPC or EAN",
        kind: "text",
        tier: "recommended",
        help: "The barcode number. Strongly recommended for shopping surfaces.",
      },
      { key: "mpn", label: "Manufacturer part number", kind: "text", tier: "recommended" },
      offersField("required"),
      aggregateRating,
    ],
  },
  {
    id: "Review",
    type: "Review",
    label: "Review",
    group: "Commerce",
    blurb: "One person's review of a thing.",
    richResultStatus: "limited",
    richResultNote:
      "Google does not show review snippets for a business reviewing itself. Use this for reviews of a product or a third party, on a page where the review is visible.",
    suggests: ["Organization"],
    fields: [
      {
        key: "itemReviewed",
        label: "What is being reviewed",
        kind: "group",
        tier: "required",
        itemType: "Thing",
        fields: [{ key: "name", label: "Name", kind: "text", tier: "required" }],
      },
      {
        key: "reviewRating",
        label: "Rating given",
        kind: "group",
        tier: "required",
        itemType: "Rating",
        fields: [
          { key: "ratingValue", label: "Rating", kind: "text", tier: "required", placeholder: "5" },
          { key: "bestRating", label: "Best possible", kind: "number", tier: "recommended", placeholder: "5" },
          { key: "worstRating", label: "Worst possible", kind: "number", tier: "recommended", placeholder: "1" },
        ],
      },
      { ...authorField, tier: "required" },
      { key: "reviewBody", label: "The review", kind: "textarea", tier: "recommended" },
      { key: "datePublished", label: "Date", kind: "date", tier: "recommended" },
    ],
  },
  {
    id: "SoftwareApplication",
    type: "SoftwareApplication",
    label: "Software or app",
    group: "Commerce",
    blurb: "An app, a plugin or a piece of software.",
    richResultStatus: "active",
    richResultNote: "Can earn a software app rich result showing rating and price.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "name", label: "App name", kind: "text", tier: "required" },
      {
        key: "applicationCategory",
        label: "Category",
        kind: "select",
        tier: "required",
        options: [
          { value: "BusinessApplication", label: "Business" },
          { value: "DeveloperApplication", label: "Developer tool" },
          { value: "DesignApplication", label: "Design" },
          { value: "EducationalApplication", label: "Education" },
          { value: "FinanceApplication", label: "Finance" },
          { value: "GameApplication", label: "Game" },
          { value: "HealthApplication", label: "Health" },
          { value: "MultimediaApplication", label: "Multimedia" },
          { value: "ProductivityApplication", label: "Productivity" },
          { value: "SecurityApplication", label: "Security" },
          { value: "UtilitiesApplication", label: "Utility" },
          { value: "WebApplication", label: "Web app" },
        ],
      },
      { key: "operatingSystem", label: "Runs on", kind: "text", tier: "recommended", placeholder: "Web browser" },
      { key: "description", label: "Description", kind: "textarea", tier: "recommended" },
      { key: "url", label: "App page", kind: "url", tier: "recommended" },
      { key: "softwareVersion", label: "Version", kind: "text", tier: "recommended" },
      { key: "screenshot", label: "Screenshot", kind: "url", tier: "recommended" },
      {
        key: "featureList",
        label: "What it does",
        kind: "stringList",
        tier: "ai",
        help: "One line per capability. This is often the list an answer engine reads back when someone asks what your product does.",
        placeholder: "Reads your existing page to prefill fields",
      },
      offersField("required"),
      aggregateRating,
    ],
  },
  {
    id: "Event",
    type: "Event",
    label: "Event",
    group: "Commerce",
    blurb: "Something happening at a time and place.",
    richResultStatus: "active",
    richResultNote: "Can earn event rich results and appear in the events experience.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "name", label: "Event name", kind: "text", tier: "required" },
      { key: "startDate", label: "Starts", kind: "datetime", tier: "required" },
      { key: "endDate", label: "Ends", kind: "datetime", tier: "recommended" },
      {
        key: "location",
        label: "Venue",
        kind: "group",
        tier: "required",
        itemType: "Place",
        fields: [
          { key: "name", label: "Venue name", kind: "text", tier: "required" },
          { ...postalAddress, label: "Venue address" },
        ],
      },
      {
        key: "eventAttendanceMode",
        label: "Attendance",
        kind: "select",
        tier: "recommended",
        options: [
          { value: "https://schema.org/OfflineEventAttendanceMode", label: "In person" },
          { value: "https://schema.org/OnlineEventAttendanceMode", label: "Online" },
          { value: "https://schema.org/MixedEventAttendanceMode", label: "Both" },
        ],
      },
      {
        key: "eventStatus",
        label: "Status",
        kind: "select",
        tier: "recommended",
        options: [
          { value: "https://schema.org/EventScheduled", label: "Scheduled" },
          { value: "https://schema.org/EventPostponed", label: "Postponed" },
          { value: "https://schema.org/EventCancelled", label: "Cancelled" },
          { value: "https://schema.org/EventRescheduled", label: "Rescheduled" },
          { value: "https://schema.org/EventMovedOnline", label: "Moved online" },
        ],
      },
      { key: "description", label: "Description", kind: "textarea", tier: "recommended" },
      { key: "image", label: "Image", kind: "url", tier: "recommended" },
      offersField("recommended"),
      {
        key: "performer",
        label: "Performer",
        kind: "group",
        tier: "recommended",
        itemType: "Person",
        fields: [{ key: "name", label: "Name", kind: "text", tier: "required" }],
      },
    ],
  },
  {
    id: "JobPosting",
    type: "JobPosting",
    label: "Job posting",
    group: "Commerce",
    blurb: "An open role you are hiring for.",
    richResultStatus: "active",
    richResultNote: "Required to appear in the Google jobs experience.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "title", label: "Job title", kind: "text", tier: "required" },
      {
        key: "description",
        label: "Full description",
        kind: "textarea",
        tier: "required",
        help: "The complete job description, matching what is on the page.",
      },
      { key: "datePosted", label: "Date posted", kind: "date", tier: "required" },
      {
        key: "hiringOrganization",
        label: "Hiring company",
        kind: "group",
        tier: "required",
        itemType: "Organization",
        fields: [
          { key: "name", label: "Company name", kind: "text", tier: "required" },
          { key: "sameAs", label: "Company website", kind: "url", tier: "recommended" },
        ],
      },
      {
        key: "jobLocation",
        label: "Location",
        kind: "group",
        tier: "required",
        itemType: "Place",
        fields: [{ ...postalAddress, label: "Work address" }],
      },
      { key: "validThrough", label: "Closes", kind: "date", tier: "recommended" },
      {
        key: "employmentType",
        label: "Employment type",
        kind: "select",
        tier: "recommended",
        options: [
          { value: "FULL_TIME", label: "Full time" },
          { value: "PART_TIME", label: "Part time" },
          { value: "CONTRACTOR", label: "Contract" },
          { value: "TEMPORARY", label: "Temporary" },
          { value: "INTERN", label: "Internship" },
          { value: "VOLUNTEER", label: "Volunteer" },
          { value: "OTHER", label: "Other" },
        ],
      },
      {
        key: "baseSalary",
        label: "Salary",
        kind: "group",
        tier: "recommended",
        itemType: "MonetaryAmount",
        fields: [
          { key: "currency", label: "Currency", kind: "text", tier: "required", placeholder: "USD" },
          {
            key: "value.minValue",
            label: "From",
            kind: "number",
            tier: "required",
            placeholder: "60000",
          },
          { key: "value.maxValue", label: "To", kind: "number", tier: "recommended", placeholder: "80000" },
          {
            key: "value.unitText",
            label: "Per",
            kind: "select",
            tier: "required",
            options: [
              { value: "YEAR", label: "Year" },
              { value: "MONTH", label: "Month" },
              { value: "WEEK", label: "Week" },
              { value: "DAY", label: "Day" },
              { value: "HOUR", label: "Hour" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "Course",
    type: "Course",
    label: "Course",
    group: "Commerce",
    blurb: "A class or training programme.",
    richResultStatus: "active",
    richResultNote: "Can appear in the course list experience.",
    suggests: ["Organization", "WebSite"],
    fields: [
      { key: "name", label: "Course name", kind: "text", tier: "required" },
      { key: "description", label: "Description", kind: "textarea", tier: "required" },
      {
        key: "provider",
        label: "Provider",
        kind: "group",
        tier: "required",
        itemType: "Organization",
        fields: [
          { key: "name", label: "Provider name", kind: "text", tier: "required" },
          { key: "sameAs", label: "Provider website", kind: "url", tier: "recommended" },
        ],
      },
      { key: "url", label: "Course page", kind: "url", tier: "recommended" },
      { key: "image", label: "Image", kind: "url", tier: "recommended" },
      offersField("recommended"),
    ],
  },

  // -- Site plumbing --------------------------------------------------------
  {
    id: "WebSite",
    type: "WebSite",
    label: "Website",
    group: "Site structure",
    blurb: "The site itself. One per site, and everything else hangs off it.",
    richResultStatus: "retired",
    richResultNote:
      "Google retired the sitelinks search box in November 2024, so the search action no longer earns a visible result. Keep this type anyway: it is the spine of the graph, and the node everything else hangs off.",
    fields: [
      { key: "name", label: "Site name", kind: "text", tier: "required" },
      { key: "url", label: "Homepage", kind: "url", tier: "required" },
      { key: "description", label: "What the site is about", kind: "textarea", tier: "ai" },
      {
        key: "inLanguage",
        label: "Language",
        kind: "text",
        tier: "recommended",
        placeholder: "en-US",
      },
      {
        key: "@searchUrl",
        label: "Site search URL pattern",
        kind: "url",
        tier: "recommended",
        placeholder: "https://yoursite.com/?s={search_term_string}",
        help: "Optional, and worth less than it used to be: Google retired the sitelinks search box in November 2024. If you add it, leave the {search_term_string} placeholder in place. That is the one placeholder that belongs in the output.",
      },
    ],
  },
  {
    id: "WebPage",
    type: "WebPage",
    label: "Web page",
    group: "Site structure",
    blurb: "This specific page, tied back to the site and the business.",
    richResultStatus: "none",
    richResultNote:
      "No rich result of its own. It is the join that connects the page to the site and the organization, which is what stops each blob floating free.",
    fields: [
      { key: "name", label: "Page title", kind: "text", tier: "required" },
      { key: "url", label: "Page URL", kind: "url", tier: "required" },
      { key: "description", label: "Page summary", kind: "textarea", tier: "ai" },
      { key: "datePublished", label: "Published", kind: "datetime", tier: "recommended" },
      { key: "dateModified", label: "Last updated", kind: "datetime", tier: "recommended" },
      { key: "primaryImageOfPage", label: "Main image", kind: "url", tier: "recommended" },
    ],
  },
  {
    id: "BreadcrumbList",
    type: "BreadcrumbList",
    label: "Breadcrumbs",
    group: "Site structure",
    blurb: "The path from the homepage to this page.",
    richResultStatus: "active",
    richResultNote:
      "Replaces the raw URL in search results with a readable trail. Cheap to add and it applies to almost every page.",
    fields: [
      {
        key: "itemListElement",
        label: "Trail",
        kind: "list",
        tier: "required",
        itemType: "ListItem",
        addLabel: "Add crumb",
        initialRows: 2,
        help: "In order, starting at the homepage.",
        fields: [
          { key: "name", label: "Label", kind: "text", tier: "required", placeholder: "Home" },
          { key: "item", label: "URL", kind: "url", tier: "required", placeholder: "https://yoursite.com/" },
        ],
      },
    ],
  },
  {
    id: "Person",
    type: "Person",
    label: "Person",
    group: "Site structure",
    blurb: "An individual: a founder, an author, a practitioner.",
    richResultStatus: "none",
    richResultNote:
      "No rich result, but it is how an answer engine ties a named human to your organization and to what they wrote.",
    suggests: ["Organization"],
    fields: [
      { key: "name", label: "Full name", kind: "text", tier: "required" },
      { key: "url", label: "Profile or bio page", kind: "url", tier: "recommended" },
      { key: "image", label: "Photo", kind: "url", tier: "recommended" },
      { key: "jobTitle", label: "Job title", kind: "text", tier: "recommended" },
      {
        key: "worksFor",
        label: "Works for",
        kind: "group",
        tier: "recommended",
        itemType: "Organization",
        fields: [{ key: "name", label: "Organization", kind: "text", tier: "required" }],
      },
      { key: "description", label: "Short bio", kind: "textarea", tier: "ai" },
      { key: "email", label: "Email", kind: "email", tier: "recommended" },
      sameAs,
    ],
  },
];

export const SCHEMA_TYPE_GROUPS = [
  "Business",
  "Content",
  "Commerce",
  "Site structure",
] as const;

/**
 * Whether a type describes this one page or the whole site.
 *
 * The builder used to ask "What is this page?" and then list Organization and
 * WebSite among the answers, which is incoherent: those are the same on every
 * page of the site and have nothing to do with the page in front of you. Scope
 * is what lets the form ask the page question and the site question separately,
 * and ask the site one once.
 *
 *   business — who you are. Collected as plain facts, not picked from a list.
 *   page     — what this particular URL is. The real question.
 *   site     — true across the whole site. Sensible defaults, set once.
 */
export type TypeScope = "business" | "page" | "site";

export const TYPE_SCOPE: Record<string, TypeScope> = {
  LocalBusiness: "business",
  Organization: "business",

  Service: "page",
  Article: "page",
  FAQPage: "page",
  HowTo: "page",
  Recipe: "page",
  VideoObject: "page",
  Product: "page",
  Review: "page",
  SoftwareApplication: "page",
  Event: "page",
  JobPosting: "page",
  Course: "page",

  WebSite: "site",
  WebPage: "site",
  BreadcrumbList: "site",
  Person: "site",
};

export function scopeOf(typeId: string): TypeScope {
  return TYPE_SCOPE[typeId] ?? "page";
}

/**
 * Site-level types in the order they should be offered, most structural first.
 *
 * Only WebSite is on by default. WebPage is genuinely useful as the join between
 * page and site, but it requires a name and a URL, and switching it on for
 * somebody who never asked would hand them two "required field missing" errors
 * before they had typed anything.
 */
export const SITE_TYPE_ORDER = ["WebSite", "WebPage", "BreadcrumbList", "Person"] as const;

/** Site types included unless the visitor turns them off. */
export const SITE_TYPE_DEFAULTS = ["WebSite"] as const;

export function getTypeSpec(id: string): SchemaTypeSpec | undefined {
  return SCHEMA_TYPES.find((t) => t.id === id);
}

/** Flatten a spec's fields, walking into groups and lists. */
export function walkFields(
  fields: FieldSpec[],
  visit: (field: FieldSpec, path: string[]) => void,
  path: string[] = []
): void {
  for (const field of fields) {
    const next = [...path, field.key];
    visit(field, next);
    if (field.fields) walkFields(field.fields, visit, next);
  }
}
